import { useState } from "react";

import { Edit } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, IconButton, Typography } from "@mui/material"

import { CreateArtefacts } from "./CreateArtefacts";

import { DrawerControl, PaginationControls } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { useFetchArtefacts } from "../hooks/useFetchArtefacts";
import { useCapabilities } from "../../../hooks/useCapabilities";

export const ProjectArtefact = ({ projectId }: { projectId: string | undefined }) => {

    const { can } = useCapabilities();
    const isAllowedAddChecklist = can("operational_delivery:project:checklist:add");
    const isAllowedEditChecklist = can("operational_delivery:project:checklist:edit");

    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(25);

    const { isPending, artefactsData, artefactsPagination, refetch: refetchArtefacts } = useFetchArtefacts(projectId, page, limit, '');

    const changePage = (page: number) => {
        setPage(page);
    };

    const changeRowsPerPage = (limit: number) => {
        setLimit(limit);
    };

    const [formStatus, setFormStatus] = useState(false);

    const [editStatus, setEditStatus] = useState<{ status: boolean; id: string | null }>({
        status: false,
        id: null
    });

    if (isPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box display={'flex'} flexDirection={'column'} alignItems={'end'} overflow={'auto'} flex={1} pl={2}>

            <Box flex={1} display={'flex'} flexDirection={'column'} gap={2} width={'100%'} overflow={'auto'} mb={2} pr={2}>
                {
                    isAllowedAddChecklist && (
                        <Button onClick={() => setFormStatus(true)} variant="outlined">Create Artefact</Button>
                    )
                }
                {
                    (!artefactsData || artefactsData.length === 0) ?
                        <Box
                            height="100%"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            width="100%"
                            gap={2}
                        >
                            <Typography variant="subtitle1">No artefacts found.</Typography>
                        </Box>
                        :
                        artefactsData.map((artefact: { id: number; title: string; category: string; assigned_to: string; created_at: string | Date | null; description: string }) => (
                            <Box
                                key={artefact.id}
                                border="1px solid"
                                borderColor="divider"
                                borderRadius={1}
                                p={2}
                            >
                                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} flexWrap={'wrap'} gap={1}>
                                    <Typography variant="subtitle2" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '80%' }}>{artefact.title}</Typography>
                                    {
                                        isAllowedEditChecklist && (
                                            <IconButton onClick={() => {
                                                setEditStatus({ status: true, id: String(artefact.id) });
                                                setFormStatus(true);
                                            }} sx={{ color: 'primary.main' }}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                </Box>
                                <Typography variant="caption">{artefact.description}</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} gap={1}>
                                    <Typography variant="caption">Category : {artefact.category}</Typography>
                                    <Typography className="caption">|</Typography>
                                    <Typography variant="caption">Owner : {artefact.assigned_to}</Typography>
                                    <Typography className="caption">|</Typography>
                                    <Typography variant="caption">Created at : {formatDate(artefact.created_at)}</Typography>
                                </Box>
                            </Box>
                        ))
                }
            </Box>

            {
                (!artefactsData || artefactsData.length === 0) ? null :
                    <Box flexShrink={0}>
                        <PaginationControls
                            total={artefactsPagination?.total}
                            rowsPerPage={artefactsPagination?.limit}
                            currentPage={artefactsPagination?.page}
                            handlePageChange={changePage}
                            handleRowsPerPage={changeRowsPerPage}
                        />
                    </Box>
            }

            <DrawerControl
                title={"Create/Edit Artefact Requirement"}
                subTitle={""}
                isOpen={formStatus}
                handleClose={() => setFormStatus(false)}
                children={
                    <CreateArtefacts
                        projectId={String(projectId)}
                        isEdit={editStatus.status}
                        artefactId={editStatus.id}
                        onClose={() => setFormStatus(false)}
                        onSave={() => {
                            setEditStatus({ status: false, id: null });
                            setFormStatus(false);
                            refetchArtefacts();
                        }}
                    />
                }
            />

        </Box>
    )
}